import { Toast, ToastBody, ToastTitle, useToastController, type ToastIntent } from '@fluentui/react-components';
import React, { createContext, ReactNode, useContext } from 'react';

export interface ToastOptions {
  intent?: ToastIntent;
  duration?: number;
}

interface ToastContextType {
  showToast: (title: string, body?: string, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children, toasterId }: { children: ReactNode; toasterId: string }) {
  const { dispatchToast } = useToastController(toasterId);

  const showToast = (title: string, body?: string, options?: ToastOptions) => {
    const { intent = 'info', duration = 5000 } = options || {};

    const toast = React.createElement(
      Toast,
      null,
      React.createElement(ToastTitle, null, title),
      body && React.createElement(ToastBody, null, body),
    );

    dispatchToast(toast, { intent, timeout: duration });
  };

  return <ToastContext.Provider value={{ showToast }}>{children}</ToastContext.Provider>;
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  return {
    success: (title: string, body?: string) => context.showToast(title, body, { intent: 'success' }),
    error: (title: string, body?: string) => context.showToast(title, body, { intent: 'error' }),
    warning: (title: string, body?: string) => context.showToast(title, body, { intent: 'warning' }),
    info: (title: string, body?: string) => context.showToast(title, body, { intent: 'info' }),
  };
}
